/**
 * Utility for managing consolidated live game state in localStorage
 * Follows the naming convention: @curipod_v1_live_game_state_{gameId}
 *
 * This consolidates all localStorage usage for a single game session including:
 * - Modal opened tracking
 * - Feedback given tracking
 * - Strength revealed tracking
 * - Any other game-specific state
 *
 * State expires after 24 hours or can be cleared when game ends
 */

const STORAGE_PREFIX = '@curipod_v1_live_game_state_';
const EXPIRY_HOURS = 24;

interface LiveGameState {
  // Track which slides have had modals opened
  modalOpened: Record<string, boolean>; // key: slideId, value: true
  // Track which slides have had feedback given
  feedbackGiven: Record<string, boolean>; // key: slideId, value: true
  // Track which responses have had strength revealed
  strengthRevealed: Record<string, boolean>; // key: responseId, value: true
  // Track which slides should open first response (temporary flag)
  openFirstResponse: Record<string, boolean>; // key: slideId, value: true
  // Track which slides have started AI feedback generation
  hasStartedGeneratingAiFeedback: Record<string, boolean>; // key: slideId, value: true
  // Track which slides have started strength detection
  hasStartedDetectingStrengths: Record<string, boolean>; // key: slideId, value: true
  // Track which slides have had the great-picks generating overlay skipped by the host
  greatPicksOverlaySkipped?: Record<string, boolean>; // key: slideId, value: true
  // Expiry timestamp
  expiresAt: number;
}

/**
 * Get the localStorage key for a game
 */
const getStorageKey = (gameId: string): string => {
  return `${STORAGE_PREFIX}${gameId}`;
};

/**
 * Check if state has expired
 */
const isExpired = (state: LiveGameState): boolean => {
  return Date.now() > state.expiresAt;
};

/**
 * Create a new state object with expiry
 */
const createNewState = (): LiveGameState => {
  return {
    modalOpened: {},
    feedbackGiven: {},
    strengthRevealed: {},
    openFirstResponse: {},
    hasStartedGeneratingAiFeedback: {},
    hasStartedDetectingStrengths: {},
    greatPicksOverlaySkipped: {},
    expiresAt: Date.now() + EXPIRY_HOURS * 60 * 60 * 1000,
  };
};

/**
 * Get the current state for a game, or create a new one if it doesn't exist or is expired
 */
const getState = (gameId: string): LiveGameState => {
  const key = getStorageKey(gameId);
  const stored = localStorage.getItem(key);

  if (!stored) {
    const newState = createNewState();
    localStorage.setItem(key, JSON.stringify(newState));
    return newState;
  }

  try {
    const state: LiveGameState = JSON.parse(stored);
    if (isExpired(state)) {
      const newState = createNewState();
      localStorage.setItem(key, JSON.stringify(newState));
      return newState;
    }
    return state;
  } catch (e) {
    // If parsing fails, create new state
    const newState = createNewState();
    localStorage.setItem(key, JSON.stringify(newState));
    return newState;
  }
};

/**
 * Save state to localStorage
 */
const saveState = (gameId: string, state: LiveGameState): void => {
  const key = getStorageKey(gameId);
  localStorage.setItem(key, JSON.stringify(state));
};

/**
 * Clear all state for a game (e.g., when game ends)
 */
export const clearLiveGameState = (gameId: string): void => {
  const key = getStorageKey(gameId);
  localStorage.removeItem(key);
};

/**
 * Check if a modal has been opened for a slide
 */
export const hasModalBeenOpened = (gameId: string, slideId: string): boolean => {
  const state = getState(gameId);
  return state.modalOpened[slideId] === true;
};

/**
 * Mark a modal as opened for a slide
 */
export const setModalOpened = (gameId: string, slideId: string): void => {
  const state = getState(gameId);
  state.modalOpened[slideId] = true;
  saveState(gameId, state);
};

/**
 * Check if feedback has been given for a slide
 */
export const hasFeedbackBeenGiven = (gameId: string, slideId: string): boolean => {
  const state = getState(gameId);
  return state.feedbackGiven[slideId] === true;
};

/**
 * Mark feedback as given for a slide
 */
export const setFeedbackGiven = (gameId: string, slideId: string): void => {
  const state = getState(gameId);
  state.feedbackGiven[slideId] = true;
  saveState(gameId, state);
};

/**
 * Check if strength has been revealed for a response
 */
export const hasStrengthBeenRevealed = (gameId: string, responseId: string): boolean => {
  const state = getState(gameId);
  return state.strengthRevealed[responseId] === true;
};

/**
 * Mark strength as revealed for a response
 */
export const setStrengthRevealed = (gameId: string, responseId: string): void => {
  const state = getState(gameId);
  state.strengthRevealed[responseId] = true;
  saveState(gameId, state);
};

/**
 * Set flag to open first response for a slide
 */
export const setOpenFirstResponse = (gameId: string, slideId: string): void => {
  const state = getState(gameId);
  state.openFirstResponse[slideId] = true;
  saveState(gameId, state);
};

/**
 * Check and clear flag to open first response for a slide
 * Returns true if flag was set, and clears it
 */
export const checkAndClearOpenFirstResponse = (
  gameId: string,
  slideId: string,
): boolean => {
  const state = getState(gameId);
  const shouldOpen = state.openFirstResponse[slideId] === true;
  if (shouldOpen) {
    delete state.openFirstResponse[slideId];
    saveState(gameId, state);
  }
  return shouldOpen;
};

/**
 * Check if AI feedback generation has started for a slide
 */
export const hasStartedGeneratingAiFeedback = (
  gameId: string,
  slideId: string,
): boolean => {
  const state = getState(gameId);
  return state.hasStartedGeneratingAiFeedback[slideId] === true;
};

/**
 * Mark AI feedback generation as started for a slide
 */
export const setHasStartedGeneratingAiFeedback = (
  gameId: string,
  slideId: string,
): void => {
  const state = getState(gameId);
  state.hasStartedGeneratingAiFeedback[slideId] = true;
  saveState(gameId, state);
};

/**
 * Check if strength detection has started for a slide
 */
export const hasStartedDetectingStrengths = (
  gameId: string,
  slideId: string,
): boolean => {
  const state = getState(gameId);
  return (state.hasStartedDetectingStrengths ?? {})[slideId] === true;
};

/**
 * Mark strength detection as started for a slide
 */
export const setHasStartedDetectingStrengths = (
  gameId: string,
  slideId: string,
): void => {
  const state = getState(gameId);
  if (!state.hasStartedDetectingStrengths) {
    state.hasStartedDetectingStrengths = {};
  }
  state.hasStartedDetectingStrengths[slideId] = true;
  saveState(gameId, state);
};

/**
 * Clear strength detection started state for a slide (used on failed requests)
 */
export const clearHasStartedDetectingStrengths = (
  gameId: string,
  slideId: string,
): void => {
  const state = getState(gameId);
  if (!state.hasStartedDetectingStrengths) {
    return;
  }
  delete state.hasStartedDetectingStrengths[slideId];
  saveState(gameId, state);
};

/**
 * Check if the host has skipped the great-picks generating overlay for a slide
 */
export const hasGreatPicksOverlaySkipped = (gameId: string, slideId: string): boolean => {
  const state = getState(gameId);
  return (state.greatPicksOverlaySkipped ?? {})[slideId] === true;
};

/**
 * Mark the great-picks generating overlay as skipped for a slide
 */
export const setGreatPicksOverlaySkipped = (gameId: string, slideId: string): void => {
  const state = getState(gameId);
  if (!state.greatPicksOverlaySkipped) {
    state.greatPicksOverlaySkipped = {};
  }
  state.greatPicksOverlaySkipped[slideId] = true;
  saveState(gameId, state);
};
